"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/use-fetch";
import { Alert, Badge, Button, Card, EmptyState, Input, Modal, PageHeader, Select, Skeleton, Textarea } from "@/components/ui";
import { useToast } from "@/components/toast";
import { useConfirm } from "@/components/confirm";

interface Ticket {
  ticketId: number;
  subject: string;
  category: string;
  priority: string;
  status: string;
  unread: boolean;
  messageCount: number;
  lastReplyRole: string | null;
  lastReplyAt: string | null;
  createdDate: string;
  updatedDate: string;
}

interface TicketMessage {
  messageId: number;
  authorRole: "USER" | "ADMIN";
  body: string;
  createdDate: string;
}

interface Thread {
  ticket: Ticket & { username?: string | null };
  messages: TicketMessage[];
}

const CATEGORIES = [
  { value: "GENERAL", label: "General" },
  { value: "TECHNICAL", label: "Technical" },
  { value: "BILLING", label: "Billing" },
  { value: "SENDER_ID", label: "Sender IDs" },
  { value: "OTHER", label: "Other" }
];
const PRIORITIES = [
  { value: "LOW", label: "Low" },
  { value: "NORMAL", label: "Normal" },
  { value: "HIGH", label: "High" },
  { value: "URGENT", label: "Urgent" }
];

const STATUS_TONE: Record<string, "gray" | "green" | "red" | "blue" | "amber"> = {
  OPEN: "amber",
  ANSWERED: "blue",
  RESOLVED: "green",
  CLOSED: "gray"
};
const STATUS_LABEL: Record<string, string> = {
  OPEN: "Awaiting support",
  ANSWERED: "Support replied",
  RESOLVED: "Resolved",
  CLOSED: "Closed"
};
const PRIORITY_TONE: Record<string, "gray" | "blue" | "amber" | "red"> = {
  LOW: "gray",
  NORMAL: "blue",
  HIGH: "amber",
  URGENT: "red"
};

function catLabel(v: string) {
  return CATEGORIES.find((c) => c.value === v)?.label ?? v;
}
function priLabel(v: string) {
  return PRIORITIES.find((p) => p.value === v)?.label ?? v;
}

function whenAgo(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
}

export default function SupportPage() {
  const toast = useToast();
  const confirm = useConfirm();

  // Ticket list (polls so an admin reply appears without a manual refresh).
  const { data, loading, reload } = useFetch<{ tickets: Ticket[] }>("account/tickets", { intervalMs: 20000 });
  const tickets = data?.tickets ?? [];
  // One active ticket at a time: a new one is only allowed once the current OPEN/ANSWERED ticket
  // is resolved or closed (the gateway enforces this too).
  const hasActive = tickets.some((tk) => tk.status === "OPEN" || tk.status === "ANSWERED");

  // New-ticket form
  const [newOpen, setNewOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const [priority, setPriority] = useState("NORMAL");
  const [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  // Open thread
  const [thread, setThread] = useState<Thread | null>(null);
  const [threadId, setThreadId] = useState<number | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadThread = useCallback(async (ticketId: number, silent = false) => {
    if (!silent) setLoadingThread(true);
    try {
      const t = await api<Thread>(`account/tickets/thread?ticketId=${ticketId}`);
      setThread(t);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoadingThread(false);
    }
  }, [toast]);

  function openTicket(ticketId: number) {
    setThreadId(ticketId);
    setThread(null);
    setReply("");
    void loadThread(ticketId);
  }

  function closeThread() {
    setThreadId(null);
    setThread(null);
    reload(); // refresh list unread/status after viewing
  }

  // Poll the open thread so a live reply shows up while it's on screen.
  useEffect(() => {
    if (!threadId) return;
    const timer = setInterval(() => {
      if (!document.hidden) void loadThread(threadId, true);
    }, 15000);
    return () => clearInterval(timer);
  }, [threadId, loadThread]);

  // Keep the conversation scrolled to the newest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread?.messages.length]);

  async function createTicket() {
    setError("");
    if (!subject.trim()) { setError("Please enter a subject."); return; }
    if (!message.trim()) { setError("Please describe your issue."); return; }
    setCreating(true);
    try {
      const res = await api<{ ticketId: number }>("account/tickets", {
        method: "POST",
        body: { subject: subject.trim(), category, priority, message: message.trim() }
      });
      toast.success("Ticket submitted. Our team will get back to you.");
      setNewOpen(false);
      setSubject(""); setCategory("GENERAL"); setPriority("NORMAL"); setMessage("");
      reload();
      openTicket(res.ticketId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function sendReply() {
    if (!threadId || !reply.trim()) return;
    setSending(true);
    try {
      await api("account/tickets/reply", { method: "POST", body: { ticketId: threadId, message: reply.trim() } });
      setReply("");
      await loadThread(threadId, true);
      reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function setStatus(action: "close" | "reopen") {
    if (!threadId) return;
    if (action === "close") {
      const ok = await confirm({
        title: "Close ticket",
        message: "Close this ticket? You can reopen it later if you still need help.",
        confirmLabel: "Close ticket"
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api("account/tickets/status", { method: "POST", body: { ticketId: threadId, action } });
      toast.success(action === "close" ? "Ticket closed." : "Ticket reopened.");
      await loadThread(threadId, true);
      reload();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const t = thread?.ticket;
  const closed = t?.status === "CLOSED";

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Support"
        subtitle="Report an issue or ask a question — our team will reply here."
        actions={
          <Button onClick={() => setNewOpen(true)} disabled={hasActive} title={hasActive ? "You already have an open ticket." : undefined}>
            New ticket
          </Button>
        }
      />

      {hasActive && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-200">
          You have an open ticket. Continue the conversation below — you can open a new ticket once it&apos;s resolved or closed.
        </div>
      )}

      {loading && !data ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : tickets.length === 0 ? (
        <EmptyState
          title="No tickets yet"
          description="Have a question or hit a problem? Open a ticket and we'll help you out."
          action={<Button onClick={() => setNewOpen(true)}>New ticket</Button>}
        />
      ) : (
        <Card bodyClassName="p-0">
          <ul className="divide-y divide-gray-100">
            {tickets.map((tk) => (
              <li key={tk.ticketId}>
                <button
                  onClick={() => openTicket(tk.ticketId)}
                  className="flex w-full items-start gap-4 px-5 py-4 text-left transition hover:bg-gray-50/60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{tk.subject}</span>
                      {tk.unread && <Badge tone="blue" dot>New reply</Badge>}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>#{tk.ticketId}</span>
                      <span>·</span>
                      <span>{catLabel(tk.category)}</span>
                      <span>·</span>
                      <Badge tone={PRIORITY_TONE[tk.priority] ?? "gray"}>{priLabel(tk.priority)}</Badge>
                      <span>·</span>
                      <span>{tk.messageCount} message{tk.messageCount === 1 ? "" : "s"}</span>
                      <span>·</span>
                      <span>Updated {whenAgo(tk.updatedDate)}</span>
                    </div>
                  </div>
                  <Badge tone={STATUS_TONE[tk.status] ?? "gray"} dot>{STATUS_LABEL[tk.status] ?? tk.status}</Badge>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* New ticket */}
      <Modal
        open={newOpen}
        onClose={() => (creating ? null : setNewOpen(false))}
        title="New support ticket"
        widthClass="max-w-lg"
        footer={
          <>
            <Button variant="secondary" disabled={creating} onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button disabled={creating} onClick={createTicket}>{creating ? "Submitting…" : "Submit ticket"}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value.slice(0, 200))} placeholder="Brief summary of your issue" autoFocus />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
            <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
          </div>
          <Textarea label="How can we help?" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the issue, including any message IDs or steps to reproduce." />
          {error && <Alert>{error}</Alert>}
        </div>
      </Modal>

      {/* Ticket thread */}
      <Modal
        open={threadId !== null}
        onClose={closeThread}
        title={t ? `#${t.ticketId} · ${t.subject}` : "Ticket"}
        widthClass="max-w-2xl"
        footer={
          t ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-gray-500">
                {catLabel(t.category)} · {priLabel(t.priority)} priority
              </span>
              <div className="flex gap-2">
                {closed ? (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => setStatus("reopen")}>Reopen</Button>
                ) : (
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => setStatus("close")}>Close ticket</Button>
                )}
                <Button size="sm" onClick={closeThread}>Done</Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {loadingThread && !thread ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : thread && t ? (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[t.status] ?? "gray"} dot>{STATUS_LABEL[t.status] ?? t.status}</Badge>
              <span className="text-xs text-gray-400">Opened {whenAgo(t.createdDate)}</span>
            </div>

            <div ref={scrollRef} className="max-h-[46vh] space-y-3 overflow-y-auto pr-1">
              {thread.messages.map((m) => {
                const mine = m.authorRole === "USER";
                return (
                  <div key={m.messageId} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                      mine
                        ? "rounded-br-sm bg-brand-600 text-pure-white"
                        : "rounded-bl-sm bg-gray-100 text-gray-800 dark:bg-gray-500/15 dark:text-gray-200"
                    }`}>
                      <div className={`mb-0.5 text-[11px] font-medium ${mine ? "text-brand-100" : "text-gray-500"}`}>
                        {mine ? "You" : "Support"} · {whenAgo(m.createdDate)}
                      </div>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {closed ? (
              <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-center text-xs text-gray-500 dark:bg-gray-500/10">
                This ticket is closed. Reopen it to add another reply.
              </p>
            ) : (
              <div className="mt-4">
                <Textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply…"
                />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" disabled={sending || !reply.trim()} onClick={sendReply}>
                    {sending ? "Sending…" : "Send reply"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Could not load this ticket.</p>
        )}
      </Modal>
    </div>
  );
}
